import { HttpErrorResponse } from "@angular/common/http";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { Observable, of, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { Config } from "src/app/models/config.model";
import { Country } from "src/app/models/country.model";
import { ContactInvitationInputModel, ContactInvitationModel } from "src/app/models/contact-invitation.model";
import { ConfigService } from "src/app/services/config.service";
import { SpinnerService } from "src/app/services/spinner.service";
import { phoneValidator } from "src/app/validators/phone.validator";
import { SnackbarMessageComponent } from "../../snackbar/snackbar-message/snackbar-message.component";
import { CaseModel } from "src/app/models/case.model";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ContactInvitationService } from "src/app/services/contact-invitation.service";
import { ConfirmBoxComponent, DialogData } from "../confirm-box/confirm-box.component";

@Component({
  selector: 'app-new-contact',
  templateUrl: './new-contact.component.html',
  styleUrls: ['./new-contact.component.scss']
})
export class NewContactComponent implements OnInit, OnDestroy {

  private componentDestroyed = new Subject();

  config!: Config;
  filteredCountries!: Observable<Country[]>;
  newContactForm!: FormGroup;

  case: CaseModel;
  isAdding: boolean;
  waitingMessage: string;
  confirmMessage: string;
  popUpConfirmMessage: string;

  constructor(
    private configService: ConfigService,
    private spinnerService: SpinnerService,
    private contactInvitationService: ContactInvitationService,
    private snackbar: MatSnackBar,
    private matDialog: MatDialog,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<NewContactComponent>,
    @Inject(MAT_DIALOG_DATA) public inputData: any) {

    this.confirmMessage = inputData.ConfirmMessage;
    this.waitingMessage = inputData.WaitingMessage;
    this.popUpConfirmMessage = inputData.PopUpConfirmMesage;
    this.case = inputData.Case;
    this.isAdding = inputData.IsAdding;

    this.configService.config
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (c: Config | null) => {
          if (c !== null) {
            this.config = c;
            this.filteredCountries = this.getFilteredCountries();
          }
        }
      );
  }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.componentDestroyed.next(null);
    this.componentDestroyed.unsubscribe();
  }

  getLabelValue(label: string): string {
    throw new Error("Method not implemented.");
  }

  initForm(): void {

    this.newContactForm = this.fb.group({

      OnBehalfOf: [0, Validators.required],
      FirstName: [null, [Validators.required, Validators.maxLength(100)]],
      LastName: [null, [Validators.required, Validators.maxLength(100)]],
      Email: [null, [Validators.required, Validators.email, Validators.maxLength(100)]],
      PhoneCountryCode: ['NO'],
      Phone: [null]
    });

    this.newContactForm.controls.Phone.setValidators([Validators.required, phoneValidator(this.newContactForm.controls.PhoneCountryCode)]);

    this.newContactForm.controls.PhoneCountryCode.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (value: string) => {
          this.newContactForm.controls.Phone.updateValueAndValidity(); //check if valid with new country code
        }
      )
  }

  getPhoneDisplayValueFromCountryCode(countryCode: string, withName: boolean): string {
    var displayValue: string = '';

    if (countryCode && countryCode !== '') {
      var country: Country | undefined = this.config.Countries.find(c => c.cca2 === countryCode);
      if (country) {
        if (country.callingcodes && country.callingcodes.length > 0) {
          displayValue = country.callingcodes[0];
        }
        if (withName) {
          displayValue = country.name.common + ' (' + displayValue + ')';
        }
      }
    }

    return displayValue;
  }

  getFilteredCountries(): Observable<Country[]> {
    //var codes = this.config.Countries.filter(z => z.region.toLowerCase() == 'europe').filter(x => x.callingcodes && x.callingcodes.length > 0)
    var codes = this.config.Countries.filter(x => x.callingcodes && x.callingcodes.length > 0);
    return of(codes);
  }

  addContactInvitation(): void {

    let newContactInvitation: ContactInvitationModel = this.newContactForm.getRawValue();
    newContactInvitation.PhoneCountryCode = newContactInvitation.PhoneCountryCode;
    newContactInvitation.CaseId = this.case.CaseId;
    newContactInvitation.RecipientType = this.newContactForm.controls.OnBehalfOf.value;
    newContactInvitation.RequesterIsComplainer = this.case.IsComplainer ? 1 : 0;

    let newContactInvitationInput: ContactInvitationInputModel = {
      ContactInvitation: newContactInvitation,
      IsAdding: this.isAdding
    };

    this.spinnerService.show("Lagrer kontaktperson på saken");
    this.contactInvitationService.addContactInvitation(newContactInvitationInput, this.case.CaseUid)
      .subscribe(
        () => {
          let message: string = ''; //Kontaktpersonen ble lagt til';
          if (this.isAdding) {
            message = `Invitasjon om å legge til kontaktperson sendt til ${newContactInvitation.FirstName} ${newContactInvitation.LastName}`;
          }
          else {
            message = `Invitasjon om å bytte kontaktperson sendt til ${newContactInvitation.FirstName} ${newContactInvitation.LastName}`;
          }

          this.snackbar.openFromComponent(SnackbarMessageComponent, { data: { message: message, type: 'info' } });
          //this.load(this.case.CaseUid);
        },
        (error: HttpErrorResponse) => {
          console.log(error.error.Message);

          this.snackbar.openFromComponent(SnackbarMessageComponent, { data: { message: error.error.Message, type: 'error' } });
          this.spinnerService.hide();
        },
        () => {
          console.log('Finished save case with id ' + this.case.CaseUid);
          this.spinnerService.hide();
          this.dialogRef.close(true);
        }
      );


  }

  save(): void {

    if (!this.newContactForm.valid) {
      this.newContactForm.markAllAsTouched();
      return;
    }

    this.openConfirmDialog()
    .subscribe(
      (value: boolean) => {
        
        if (value === true) {
          this.addContactInvitation();
        }
      }
    );
  }

  close(): void {
    this.dialogRef.close();
  }

  openConfirmDialog(): Observable<boolean> {

    let initialState: DialogData = {
      body: this.popUpConfirmMessage,
      title: 'Er du sikker?',
      okBtnName: 'Ok',
      closeBtnName: 'Avbryt'
    };

    const dialogRef: MatDialogRef<ConfirmBoxComponent, boolean> = this.matDialog.open(ConfirmBoxComponent, { data: initialState })
    return dialogRef.afterClosed()
    .pipe(map(result => result === undefined ? false : result));
  }


}
